import { ReactNode, useRef } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';

import { Box, Typography, SxProps, Theme } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection;

type BlueprintSectionCardProps = Readonly<{
  id: string;
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  lubavPromptButtonId?: string;
  lubavChatMessage?: string;
  children: ReactNode;
}>;

export function BlueprintSectionCard({
  id,
  title,
  isExpanded,
  onToggle,
  lubavPromptButtonId,
  lubavChatMessage,
  children,
}: BlueprintSectionCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // React events bubble through the component tree even from portaled descendants
  // (modals, popovers). Without this guard, clicks inside an open modal — or on its
  // backdrop — bubble back to the card and toggle the detail panel.
  const isClickInsideCard = (target: EventTarget | null): boolean =>
    target instanceof Node && !!cardRef.current?.contains(target);

  return (
    <Box
      ref={cardRef}
      sx={cardSx(isExpanded)}
      id={`${id}-card`}
      onClick={(e) => {
        if (!isClickInsideCard(e.target)) return;

        onToggle();
      }}
    >
      <Box
        sx={headerSx}
        id={`${id}-header`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <Typography variant="text-md-semibold" sx={titleSx}>
          {title}
        </Typography>
        <Box>
          <AppIcon
            iconName="ArrowCircleUpRight"
            diameter="20px"
            color="currentColor"
            rotate={isExpanded ? 90 : 0}
          />
        </Box>
      </Box>

      <Box sx={contentSx}>{children}</Box>

      {lubavPromptButtonId && (
        <Box
          sx={footerSx}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <LubavButton
            id={`${id}-discuss-btn`}
            label={STRINGS.discuss}
            chatMessage={lubavChatMessage}
            promptButtonId={lubavPromptButtonId}
          />
        </Box>
      )}
    </Box>
  );
}

const cardSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-lg'],
    padding: backrs.spacing['spacing-xl'],
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/500' : 'Gray/700']}`,
    backgroundColor: backrs.colors['Colors/Base/White'],
    minWidth: 0,
    cursor: 'pointer',
    color: backrs.colors['Colors/Base/Black'],
    outline: 'none',
    transition: 'border-color 0.3s ease-in-out, background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: backrs.colors['Gray/25'],
    },
  });

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = {
  color: 'inherit',
};

const contentSx: SxProps<Theme> = {
  flex: 1,
};

const footerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
};
